import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Clock, Shield, Award, ChevronRight, Heart, Users, MapPin, Star, CheckCircle2, Phone, Mail, ChevronDown, Briefcase, Home as HomeIcon, Baby, Sparkles, Wrench, Monitor, Euro, FileCheck, Headphones } from 'lucide-react';
import { ScrollReveal } from '../components/ScrollReveal';

export const Home = () => {
  const [statsInView, setStatsInView] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const statsRef = useRef(null);

  // Counter animation for statistics
  const [counters, setCounters] = useState({
    clients: 0,
    experience: 0,
    satisfaction: 0,
    interventions: 0
  });

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setStatsInView(true);
        }
      },
      { threshold: 0.3 }
    );

    if (statsRef.current) {
      observer.observe(statsRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!statsInView) return;

    const duration = 2000;
    const steps = 60;
    const interval = duration / steps;

    const targets = {
      clients: 500,
      experience: 10,
      satisfaction: 98,
      interventions: 15000
    };

    let step = 0;
    const timer = setInterval(() => {
      step++;
      const progress = step / steps;

      setCounters({
        clients: Math.floor(targets.clients * progress),
        experience: Math.floor(targets.experience * progress),
        satisfaction: Math.floor(targets.satisfaction * progress),
        interventions: Math.floor(targets.interventions * progress)
      });

      if (step >= steps) {
        clearInterval(timer);
        setCounters(targets);
      }
    }, interval);

    return () => clearInterval(timer);
  }, [statsInView]);

  const keyFeatures = [
    {
      icon: <Clock size={40} />,
      title: 'Disponibilité 7j/7',
      description: 'Service d\'aide à domicile disponible tous les jours, y compris les week-ends et jours fériés à Lyon'
    },
    {
      icon: <Euro size={40} />,
      title: '50% de Crédit d\'Impôt',
      description: 'Réduction fiscale immédiate sur tous nos services à la personne agréés par l\'État'
    },
    {
      icon: <Shield size={40} />,
      title: 'Personnel de Confiance',
      description: 'Équipe d\'auxiliaires de vie formés, vérifiés et assurés pour votre tranquillité'
    }
  ];

  const values = [
    {
      icon: <Heart size={40} />,
      title: 'Approche humaine',
      description: 'Une relation de confiance où chaque bénéficiaire est traité avec respect et bienveillance'
    },
    {
      icon: <Users size={40} />,
      title: 'Équipe qualifiée',
      description: 'Auxiliaires de vie diplômés (ADVF, DEAES) et formés en continu aux meilleures pratiques'
    },
    {
      icon: <Award size={40} />,
      title: 'Service sur-mesure',
      description: 'Plan d\'aide personnalisé élaboré selon vos besoins spécifiques et votre situation'
    }
  ];

  const services = [
    {
      title: 'Aide à la personne âgée',
      description: 'Accompagnement quotidien, aide à la toilette, préparation des repas et maintien à domicile',
      image: '/images/home-care.jpg',
      link: '/aides-personne',
      keywords: ['aide personne âgée Lyon', 'maintien à domicile', 'auxiliaire de vie']
    },
    {
      title: 'Garde d\'enfants à domicile',
      description: 'Nounou qualifiée, sortie d\'école, aide aux devoirs et garde périscolaire',
      image: '/images/childcare.jpg',
      link: '/garde-enfant',
      keywords: ['garde enfant Lyon', 'nounou domicile', 'baby-sitting']
    },
    {
      title: 'Ménage et repassage',
      description: 'Entretien ménager régulier, grand ménage, repassage et nettoyage professionnel',
      image: '/images/cleaning.jpg',
      link: '/menage',
      keywords: ['femme de ménage Lyon', 'aide ménagère', 'repassage domicile']
    },
    {
      title: 'Bricolage et jardinage',
      description: 'Petits travaux, montage meubles, entretien jardin et espaces verts',
      image: '/images/handyman.jpg',
      link: '/bricolage',
      keywords: ['bricolage domicile Lyon', 'jardinage', 'homme toutes mains']
    },
    {
      title: 'Assistance informatique',
      description: 'Initiation numérique seniors, dépannage ordinateur et aide smartphone',
      image: '/images/it-support.jpg',
      link: '/informatique',
      keywords: ['aide informatique seniors', 'cours informatique domicile', 'dépannage']
    }
  ];

  const testimonials = [
    {
      name: 'Marie D.',
      location: 'Lyon 3ème',
      service: 'Aide à la personne',
      rating: 5,
      comment: 'L\'auxiliaire de vie qui s\'occupe de ma mère est exceptionnelle. Professionnelle, ponctuelle et d\'une grande gentillesse. Ma mère l\'attend avec impatience chaque jour.',
      date: 'Décembre 2024'
    },
    {
      name: 'Jean-Pierre M.',
      location: 'Villeurbanne',
      service: 'Garde d\'enfant',
      rating: 5,
      comment: 'Notre nounou s\'occupe de nos deux enfants depuis 2 ans. Elle les récupère à l\'école, les aide pour les devoirs et prépare le goûter. Un vrai soulagement pour notre organisation familiale.',
      date: 'Novembre 2024'
    },
    {
      name: 'Sophie B.',
      location: 'Lyon 6ème',
      service: 'Ménage régulier',
      rating: 5,
      comment: 'Je fais appel à leurs services de ménage depuis 3 ans. Toujours la même intervenante, ponctuelle et minutieuse. Ma maison n\'a jamais été aussi propre !',
      date: 'Janvier 2025'
    },
    {
      name: 'Robert L.',
      location: 'Caluire',
      service: 'Assistance informatique',
      rating: 5,
      comment: 'À 78 ans, je pensais ne jamais pouvoir utiliser un smartphone. Grâce à leur patience et pédagogie, je fais maintenant des visios avec mes petits-enfants !',
      date: 'Décembre 2024'
    }
  ];

  const whyChooseUs = [
    {
      icon: <FileCheck size={32} />,
      title: 'Entreprise Agréée',
      description: 'Agrément Services à la Personne délivré par l\'État, garantie de qualité et d\'éligibilité au crédit d\'impôt'
    },
    {
      icon: <Shield size={32} />,
      title: 'Assurance Complète',
      description: 'Responsabilité civile professionnelle couvrant tous les intervenants et leurs missions'
    },
    {
      icon: <Headphones size={32} />,
      title: 'Interlocuteur Dédié',
      description: 'Un conseiller unique vous accompagne de l\'évaluation des besoins au suivi régulier'
    },
    {
      icon: <Award size={32} />,
      title: 'Remplacement Garanti',
      description: 'En cas d\'absence de votre intervenant habituel, nous assurons un remplacement immédiat'
    }
  ];

  const engagements = [
    {
      icon: <CheckCircle2 size={28} />,
      title: 'Devis gratuit sous 24h',
      description: 'Évaluation personnalisée de vos besoins à domicile'
    },
    {
      icon: <CheckCircle2 size={28} />,
      title: 'Sans engagement',
      description: 'Résiliation possible à tout moment sans frais'
    },
    {
      icon: <CheckCircle2 size={28} />,
      title: 'Facturation claire',
      description: 'Tarifs transparents, pas de frais cachés'
    },
    {
      icon: <CheckCircle2 size={28} />,
      title: 'Paiement facilité',
      description: 'CESU acceptés, prélèvement automatique possible'
    }
  ];

  const serviceDetails = [
    {
      icon: <HomeIcon size={36} />,
      title: 'Maintien à domicile',
      description: 'Nous permettons aux personnes âgées ou en situation de handicap de rester chez elles en toute sécurité grâce à un accompagnement adapté.',
      benefits: ['Aide à la toilette et à l\'habillage', 'Préparation et aide aux repas', 'Accompagnement aux sorties', 'Stimulation cognitive']
    },
    {
      icon: <Baby size={36} />,
      title: 'Solutions garde d\'enfants',
      description: 'Des solutions flexibles pour la garde de vos enfants, adaptées à votre rythme de travail et à vos besoins familiaux.',
      benefits: ['Garde régulière ou occasionnelle', 'Sortie d\'école et crèche', 'Aide aux devoirs', 'Activités ludiques et éducatives']
    },
    {
      icon: <Sparkles size={36} />,
      title: 'Entretien du logement',
      description: 'Un intérieur propre et ordonné contribue à votre bien-être. Nos aides ménagères s\'adaptent à vos exigences.',
      benefits: ['Ménage régulier ou ponctuel', 'Repassage à domicile', 'Nettoyage en profondeur', 'Entretien du linge']
    }
  ];

  const faqItems = [
    {
      question: 'Comment bénéficier du crédit d\'impôt de 50% ?',
      answer: 'En tant qu\'entreprise agréée Services à la Personne, toutes nos prestations sont éligibles au crédit d\'impôt de 50%. Nous vous fournissons chaque année une attestation fiscale à joindre à votre déclaration de revenus. Le crédit d\'impôt s\'applique que vous soyez imposable ou non.'
    },
    {
      question: 'Quelles sont vos zones d\'intervention à Lyon ?',
      answer: 'Nous intervenons dans tout Lyon (du 1er au 9ème arrondissement) ainsi que dans les communes limitrophes : Villeurbanne, Caluire-et-Cuire, Vaulx-en-Velin, Bron, Vénissieux, Saint-Priest, Oullins, Tassin-la-Demi-Lune et Écully. Contactez-nous pour vérifier si votre commune est couverte.'
    },
    {
      question: 'Comment sont sélectionnés vos intervenants ?',
      answer: 'Chaque intervenant passe par un processus de recrutement rigoureux : vérification des diplômes (ADVF, DEAES, CAP Petite Enfance...), contrôle du casier judiciaire, entretiens de motivation et période d\'essai encadrée. Nous assurons également des formations continues pour maintenir un haut niveau de compétences.'
    },
    {
      question: 'Puis-je choisir les horaires d\'intervention ?',
      answer: 'Absolument ! Nous établissons ensemble un planning personnalisé selon vos besoins et contraintes. Les horaires peuvent être modifiés à tout moment avec un préavis de 48h. Nous proposons des interventions du lundi au dimanche, y compris les jours fériés.'
    },
    {
      question: 'Que se passe-t-il si mon intervenant est absent ?',
      answer: 'En cas d\'absence prévue (congés) ou imprévue (maladie), nous organisons systématiquement un remplacement. Nous nous efforçons de vous proposer un intervenant que vous connaissez déjà ou de vous présenter le remplaçant au préalable pour assurer une continuité de service.'
    },
    {
      question: 'Quels moyens de paiement acceptez-vous ?',
      answer: 'Nous acceptons les virements bancaires, les prélèvements automatiques et les Chèques Emploi Service Universel (CESU) préfinancés. Nous pouvons également vous accompagner dans vos démarches d\'obtention de l\'APA (Allocation Personnalisée d\'Autonomie) ou de la PCH (Prestation de Compensation du Handicap).'
    },
    {
      question: 'Comment se déroule la première intervention ?',
      answer: 'Avant la première intervention, nous réalisons une visite d\'évaluation gratuite à votre domicile. Cette rencontre permet d\'identifier précisément vos besoins, de vous présenter l\'intervenant sélectionné et d\'établir ensemble le planning et les modalités de la prestation.'
    },
    {
      question: 'Proposez-vous des services pour les entreprises ?',
      answer: 'Oui, nous proposons des services de ménage et d\'entretien pour les locaux professionnels : bureaux, cabinets médicaux, commerces. Nous établissons un devis personnalisé selon la surface et la fréquence souhaitée.'
    }
  ];

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  return (
    <div className="page-home">
      {/* Hero Section with Video Background */}
      <section className="hero-section hero-video-section">
        <video 
          className="hero-video-bg" 
          autoPlay 
          muted 
          loop 
          playsInline
          poster="/images/caregiver.jpg"
        >
          <source src="/images/hero-video.mp4" type="video/mp4" />
        </video>
        <div className="hero-video-overlay"></div>
        <div className="container hero-video-content">
          <div className="hero-text-centered">
            <h1 className="display-large">
              Aide à Domicile à Lyon : Services de Qualité pour Toute la Famille
            </h1>
            <p className="hero-description body-medium">
              <strong>Réseau d'Aide à Domicile</strong>, votre partenaire de confiance depuis 2015. 
              Nous accompagnons les familles lyonnaises avec des services personnalisés : 
              aide aux personnes âgées, garde d'enfants, ménage, bricolage et assistance informatique. 
              <em> Bénéficiez de 50% de crédit d'impôt sur toutes nos prestations.</em>
            </p>
            <div className="hero-buttons">
              <Link to="/contact#devis" className="btn-primary">Demander un devis gratuit</Link>
              <a href="tel:0481119591" className="btn-secondary">
                <Phone size={18} /> 04 81 11 95 91
              </a>
            </div>
            <div className="hero-trust-badges">
              <span className="trust-badge"><CheckCircle2 size={16} /> Entreprise agréée</span>
              <span className="trust-badge"><CheckCircle2 size={16} /> 50% crédit d'impôt</span>
              <span className="trust-badge"><CheckCircle2 size={16} /> Devis gratuit 24h</span>
            </div>
          </div>
        </div>
      </section>

      {/* Key Features */}
      <section className="key-features-section">
        <div className="container">
          <div className="network-grid">
            {keyFeatures.map((feature, index) => (
              <ScrollReveal key={index} animation="fade-up" delay={index * 100}>
                <div className="network-card feature-card">
                  <div className="feature-icon">{feature.icon}</div>
                  <h3 className="heading-2">{feature.title}</h3>
                  <p className="body-medium">{feature.description}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Engagements Section */}
      <section className="engagements-section">
        <div className="container">
          <div className="engagements-grid">
            {engagements.map((item, index) => (
              <ScrollReveal key={index} animation="fade-up" delay={index * 50}>
                <div className="engagement-item">
                  {item.icon}
                  <div>
                    <strong>{item.title}</strong>
                    <span>{item.description}</span>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* About Section - Integrated */}
      <section id="about" className="about-section-home">
        <div className="container">
          <ScrollReveal animation="fade-up">
            <div className="section-header">
              <h2 className="heading-1">Réseau d'Aide à Domicile : Votre Partenaire de Confiance à Lyon</h2>
              <p className="body-medium">Une entreprise familiale au service de votre bien-être depuis 2015</p>
            </div>
          </ScrollReveal>
          
          <div className="about-grid">
            <ScrollReveal animation="fade-right" delay={200}>
              <div className="about-text">
                <h3 className="heading-2">Notre histoire et notre engagement</h3>
                <div className="about-description">
                <p className="body-medium">
                  Fondée en 2015 par Clémence Sognon, <strong>Réseau d'Aide à Domicile</strong> est une entreprise 
                  familiale lyonnaise spécialisée dans les <strong>services à la personne</strong>. Notre mission : 
                  permettre à chacun de vivre sereinement chez soi, quel que soit son âge ou sa situation.
                </p>
                <p className="body-medium">
                  Nous avons construit notre réputation sur des valeurs fortes : <strong>écoute, bienveillance 
                  et professionnalisme</strong>. Chaque client bénéficie d'un accompagnement personnalisé, 
                  avec un interlocuteur dédié qui suit son dossier de A à Z.
                </p>
                <p className="body-medium">
                  Notre équipe d'<strong>auxiliaires de vie diplômés</strong> (ADVF, DEAES) intervient quotidiennement 
                  auprès de personnes âgées, de familles avec enfants et de personnes en situation de handicap 
                  dans tout Lyon et sa métropole.
                </p>
              </div>
            </div>
            </ScrollReveal>
            <ScrollReveal animation="fade-left" delay={300}>
              <div className="about-image">
                <img 
                  src="/images/apropos.jpg" 
                  alt="Équipe Réseau d'Aide à Domicile Lyon" 
                />
              </div>
            </ScrollReveal>
          </div>

          <div className="values-grid">
            <ScrollReveal animation="fade-up">
              <h3 className="heading-2 text-center">Nos valeurs fondamentales</h3>
            </ScrollReveal>
            <div className="network-grid">
              {values.map((value, index) => (
                <ScrollReveal key={index} animation="fade-up" delay={index * 100}>
                  <div className="network-card">
                    <div className="feature-icon">{value.icon}</div>
                    <h4 className="heading-2">{value.title}</h4>
                    <p className="body-medium">{value.description}</p>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="services-section">
        <div className="container">
          <ScrollReveal animation="fade-up">
            <div className="section-header">
              <h2 className="heading-1">Nos Services d'Aide à Domicile à Lyon</h2>
              <p className="body-medium">Des solutions complètes pour répondre à tous vos besoins quotidiens</p>
            </div>
          </ScrollReveal>
          <div className="services-grid">
            {services.map((service, index) => (
              <ScrollReveal key={index} animation="fade-up" delay={index * 100}>
                <Link to={service.link} className="service-card">
                  <div className="service-image">
                    <img src={service.image} alt={`${service.title} - Service à domicile Lyon`} />
                </div>
                <div className="service-content">
                  <h3 className="heading-2">{service.title}</h3>
                  <p className="body-medium">{service.description}</p>
                  <span className="service-link">
                    En savoir plus <ChevronRight size={20} />
                  </span>
                </div>
              </Link>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Service Details Section */}
      <section className="service-details-section">
        <div className="container">
          <ScrollReveal animation="fade-up">
            <div className="section-header">
              <h2 className="heading-1">Des Services Adaptés à Chaque Situation</h2>
              <p className="body-medium">Découvrez comment nous pouvons vous accompagner au quotidien</p>
            </div>
          </ScrollReveal>
          <div className="service-details-grid">
            {serviceDetails.map((service, index) => (
              <ScrollReveal key={index} animation="fade-up" delay={index * 150}>
                <div className="service-detail-card">
                  <div className="service-detail-icon">{service.icon}</div>
                  <h3 className="heading-2">{service.title}</h3>
                  <p className="body-medium">{service.description}</p>
                  <ul className="service-benefits">
                    {service.benefits.map((benefit, i) => (
                      <li key={i}><CheckCircle2 size={16} /> {benefit}</li>
                    ))}
                  </ul>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Statistics Section - Animated */}
      <section ref={statsRef} className="stats-section">
        <div className="container">
          <div className="section-header stats-header">
            <h2 className="heading-1">Réseau d'Aide à Domicile en Chiffres</h2>
          </div>
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-number">{counters.clients}+</div>
              <div className="stat-label">Familles Accompagnées</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">{counters.experience}</div>
              <div className="stat-label">Années d'Expérience</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">{counters.satisfaction}%</div>
              <div className="stat-label">Clients Satisfaits</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">{counters.interventions.toLocaleString()}+</div>
              <div className="stat-label">Interventions Réalisées</div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="why-choose-section">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-1">Pourquoi Choisir Réseau d'Aide à Domicile ?</h2>
            <p className="body-medium">Les garanties qui font la différence pour votre tranquillité</p>
          </div>
          <div className="network-grid">
            {whyChooseUs.map((item, index) => (
              <div key={index} className="network-card why-card fade-in-up">
                <div className="feature-icon">{item.icon}</div>
                <h3 className="heading-2">{item.title}</h3>
                <p className="body-medium">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Location Section */}
      <section className="location-section">
        <div className="container">
          <div className="location-content">
            <div className="location-text">
              <MapPin size={48} className="location-icon" />
              <h2 className="heading-1">Aide à Domicile à Lyon et dans la Métropole</h2>
              <p className="body-medium">
                Basés au cœur de Lyon (69003), nous intervenons dans toute la métropole lyonnaise. 
                Nos équipes locales connaissent parfaitement le territoire et peuvent intervenir 
                rapidement à votre domicile, du lundi au dimanche.
              </p>
              <div className="location-zones">
                <div className="zone-badge">Lyon 1er - 9ème</div>
                <div className="zone-badge">Villeurbanne</div>
                <div className="zone-badge">Caluire-et-Cuire</div>
                <div className="zone-badge">Vaulx-en-Velin</div>
                <div className="zone-badge">Bron</div>
                <div className="zone-badge">Vénissieux</div>
                <div className="zone-badge">Saint-Priest</div>
                <div className="zone-badge">Oullins</div>
              </div>
              <div className="location-address">
                <strong>Notre adresse :</strong><br />
                23 rue du Dauphiné, 69003 Lyon
              </div>
              <div className="location-contact">
                <div className="contact-info-item">
                  <Phone size={20} />
                  <a href="tel:0481119591">04 81 11 95 91</a>
                </div>
                <div className="contact-info-item">
                  <Mail size={20} />
                  <a href="mailto:contact@reseaudaideadomicile.fr">contact@reseaudaideadomicile.fr</a>
                </div>
              </div>
            </div>
            <div className="location-image">
              <div className="location-badge-overlay">
                <div className="badge-item">
                  <MapPin size={24} />
                  <span>Intervention 7j/7</span>
                </div>
                <div className="badge-item">
                  <Clock size={24} />
                  <span>Réponse sous 24h</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="testimonials-section">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-1">Avis de Nos Clients à Lyon</h2>
            <p className="body-medium">Découvrez les témoignages de familles que nous accompagnons au quotidien</p>
          </div>
          <div className="testimonials-grid">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="testimonial-card fade-in-up">
                <div className="testimonial-rating">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} size={20} fill="var(--brand-primary)" color="var(--brand-primary)" />
                  ))}
                </div>
                <p className="testimonial-comment">"{testimonial.comment}"</p>
                <div className="testimonial-author">
                  <div className="author-info">
                    <strong>{testimonial.name}</strong>
                    <span>{testimonial.service} • {testimonial.location}</span>
                  </div>
                  <span className="testimonial-date">{testimonial.date}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq-section">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-1">Questions Fréquentes sur l'Aide à Domicile</h2>
            <p className="body-medium">Tout ce que vous devez savoir sur nos services</p>
          </div>
          <div className="faq-list">
            {faqItems.map((item, index) => (
              <div key={index} className={`faq-item ${openFaq === index ? 'open' : ''}`}>
                <button className="faq-question" onClick={() => toggleFaq(index)}>
                  <span>{item.question}</span>
                  <ChevronDown size={24} className="faq-icon" />
                </button>
                <div className="faq-answer">
                  <p>{item.answer}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2 className="heading-1">Besoin d'une Aide à Domicile à Lyon ?</h2>
            <p className="body-medium">
              Contactez-nous dès aujourd'hui pour un devis gratuit et personnalisé. 
              Notre équipe vous répond sous 24h pour évaluer vos besoins.
            </p>
            <div className="cta-buttons">
              <Link to="/contact#devis" className="btn-primary btn-cta">Demander un devis gratuit</Link>
              <a href="tel:0481119591" className="btn-secondary btn-cta">
                <Phone size={20} /> Appeler le 04 81 11 95 91
              </a>
            </div>
            <p className="cta-note">
              <CheckCircle2 size={16} /> Devis gratuit et sans engagement • 50% crédit d'impôt
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};
